/**
 * Token management utilities for AG-UI Chat.
 *
 * Handles fetching and caching JWT tokens for authenticated AG-UI requests.
 */

export interface TokenResponse {
  token: string;
  expires_at: number;
  expires_in: number;
  uuid?: string;
  error?: string | null;
}

export interface TokenConfig {
  /** URL to fetch token from (defaults to /agui/api/token) */
  tokenEndpoint?: string;
  /** Buffer time in seconds before expiry to refresh token (defaults to 60) */
  refreshBuffer?: number;
}


/**
 * Manages authentication tokens for AG-UI requests.
 */
export class TokenManager {
  private static readonly ANON_USER_ID_KEY = 'agui_anon_uuid';

  private token: string | null = null;
  private expiresAt: number = 0;
  private threadId: string | null = null;
  private userId: string | null = null;
  private tokenEndpoint: string;
  private refreshBuffer: number;
  private fetchPromise: Promise<string> | null = null;

  constructor(config: TokenConfig = {}) {
    this.tokenEndpoint = config.tokenEndpoint || '/agui/api/token';
    this.refreshBuffer = config.refreshBuffer || 60;

    // Restore anonymous user ID from sessionStorage if available
    this.userId = this.getStoredAnonymousUserId();
  }

  /**
   * Gets a valid token, fetching a new one if necessary.
   * Returns null if token fetching is disabled (no endpoint configured).
   */
  async getToken(): Promise<string | null> {
    return this.getTokenForThread();
  }

  /**
   * Gets a token, optionally with thread-specific claims for SSE subscription.
   * Caches the token and only fetches a new one if:
   * - threadId changed
   * - token expired
   *
   * @param threadId - Optional thread ID to include in token claims
   */
  async getTokenForThread(threadId?: string): Promise<string | null> {
    // Check if we have a valid cached token
    if (this.isTokenValid(threadId)) {
      return this.token;
    }

    // If a fetch is already in progress, wait for it
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Fetch a new token
    this.threadId = threadId ?? null;
    this.fetchPromise = this.fetchToken(threadId);

    try {
      const token = await this.fetchPromise;
      return token;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Checks if the current token is still valid for the given threadId.
   */
  private isTokenValid(threadId?: string): boolean {
    if (!this.token) {
      return false;
    }

    // Token is invalid if threadId changed
    if ((threadId ?? null) !== this.threadId) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    // Consider token invalid if it expires within the refresh buffer
    return this.expiresAt > now + this.refreshBuffer;
  }

  /**
   * Fetches a new token from the endpoint.
   *
   * @param threadId - Optional thread ID to include in the request
   */
  private async fetchToken(threadId?: string): Promise<string> {
    try {
      // Build URL with optional query params
      const url = new URL(this.tokenEndpoint, window.location.origin);
      if (threadId) {
        url.searchParams.set('threadId', threadId);
      }

      // Pass existing anonymous user ID for session continuity across token refreshes.
      // The server only uses this for anonymous users; authenticated users get their
      // UUID from the database regardless of what we send here.
      const existingAnonId = this.getStoredAnonymousUserId();
      if (existingAnonId) {
        url.searchParams.set('anonymous_id', existingAnonId);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Token fetch failed: ${response.status}`);
      }

      const data: TokenResponse = await response.json();

      if (data.error) {
        throw new Error(data.error as unknown as string);
      }

      this.token = data.token;
      this.expiresAt = data.expires_at;
      this.userId = data.uuid ?? null;

      // Store user_id in sessionStorage for anonymous session continuity.
      // This is safe to call for authenticated users too - the server ignores
      // the anonymous_id param for authenticated requests, so it's harmless.
      if (this.userId) {
        this.storeAnonymousUserId(this.userId);
      }

      return data.token;
    } catch (error) {
      console.error('Failed to fetch AG-UI token:', error);
      throw error;
    }
  }

  /**
   * Clears the cached token.
   */
  clearToken(): void {
    this.token = null;
    this.expiresAt = 0;
    this.threadId = null;
    this.userId = null;
  }

  /**
   * Gets the user ID (UUID) from the last token response.
   */
  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Checks if token authentication is enabled.
   * This can be used to conditionally skip token fetching for local endpoints.
   */
  isEnabled(): boolean {
    return Boolean(this.tokenEndpoint);
  }

  /**
   * Gets the stored anonymous user ID from sessionStorage.
   */
  private getStoredAnonymousUserId(): string | null {
    try {
      return sessionStorage.getItem(TokenManager.ANON_USER_ID_KEY);
    } catch {
      // sessionStorage may not be available (e.g., private browsing)
      return null;
    }
  }

  /**
   * Stores the anonymous user ID in sessionStorage.
   */
  private storeAnonymousUserId(userId: string): void {
    try {
      sessionStorage.setItem(TokenManager.ANON_USER_ID_KEY, userId);
    } catch {
      // sessionStorage may not be available (e.g., private browsing)
    }
  }
}

/**
 * Creates authorization headers with the bearer token.
 */
export function createAuthHeaders(token: string | null): Record<string, string> {
  if (!token) {
    return {};
  }

  return {
    'Authorization': `Bearer ${token}`,
  };
}

